import {
    ContentType,
    GeneralDetailError,
    ICorporationResponse,
    ILocationResponse,
    IPictureFileMetadataResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { mutationOptions, useMutation, UseMutationOptions } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { useRootFolder } from 'custom_hooks/data_hooks/file/useRootFolder';
import {
    defaultMaxFileNameLength,
    defaultMaxFileSize,
    FileNameTooLongError,
    FileTooLargeError,
    FileUpload,
    isValidPicture,
    UnsupportedFileTypeError,
} from 'pages/Settings/FileLibrary/shared';

type FileUploadMutationConfig = {
    groupIds?: string[];
    locationId?: string;
    corporationId?: string;
    parentFolderId?: string;
    maxFileSize?: number;
    maxFileNameLength?: number;
};

export const fileUploadMutations = {
    upload: ({
        groupIds,
        locationId,
        corporationId,
        parentFolderId,
        maxFileSize = defaultMaxFileSize,
        maxFileNameLength = defaultMaxFileNameLength,
    }: FileUploadMutationConfig) =>
        mutationOptions({
            mutationKey: [
                'file-upload',
                { groupIds, locationId, corporationId },
            ] as const,
            mutationFn: (file: FileUpload): Promise<IPictureFileMetadataResponse> => {
                if (!isValidPicture(file.data)) {
                    throw new UnsupportedFileTypeError();
                }
                if (file.data.size > maxFileSize) {
                    throw new FileTooLargeError();
                }
                if (file.name.length > maxFileNameLength) {
                    throw new FileNameTooLongError();
                }
                return sdClient.createFile({
                    fileType: ContentType.Picture,
                    displayName: file.name,
                    parentFolderId,
                    locationId,
                    corporationId,
                    groupIds,
                    file: {
                        data: file.data,
                        fileName: file.name,
                    },
                });
            },
        }),
};

export type UseFileUpload = {
    /**
     * The corporation to upload the file to.
     */
    corporation?: ICorporationResponse;
    /**
     * The location to upload the file to.
     */
    location?: ILocationResponse;
    /**
     * The group Ids to associate with the file
     */
    groupIds?: string[];
    /**
     * The maximum file size (in bytes) that can be uploaded.
     *
     * NOTE: This is just client-side validation, the file size must still be
     * validated on the server.
     */
    maxFileSize?: number;
    /**
     * The maximum file name length.
     *
     * NOTE: This is just client-side validation, the file name length must still
     * be validated on the server.
     */
    maxFileNameLength?: number;
    /**
     * Parent Folder's Id. If unsupplied, will use the Root folder's id
     */
    parentFolderId?: string;
};

type UseFileUploadOptions = UseMutationOptions<
    IPictureFileMetadataResponse,
    GeneralDetailError | Error,
    FileUpload
>;

export function useFileUpload(
    {
        location,
        corporation,
        groupIds,
        maxFileSize,
        maxFileNameLength,
        parentFolderId,
    }: UseFileUpload,
    options?: UseFileUploadOptions
) {
    const { data: rootFolder } = useRootFolder({
        corporationId: corporation?.Id,
        locationId: location?.Id,
    });

    return useMutation({
        ...fileUploadMutations.upload({
            groupIds,
            locationId: location?.Id,
            corporationId: corporation?.Id,
            parentFolderId: parentFolderId ?? rootFolder?.Id,
            maxFileSize,
            maxFileNameLength,
        }),
        ...options,
    });
}
